import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { FileText, Palette, X } from "lucide-react";

const NOTE_COLORS = [
    "#064c7b", "#4EBCE4", "#10b981", "#84cc16",
    "#f59e0b", "#f97316", "#ef4444", "#ec4899",
    "#a855f7", "#6366f1", "#0ea5e9", "#14b8a6"
];

const getNoteColorToken = (noteColor) => noteColor.replace("#", "").toLowerCase();

function EditNotePopup({ editedNote, editedColor, closePopup, confirm, userCapability }) {
    const [note, setNote] = useState(editedNote);
    const [color, setColor] = useState(editedColor || "#064c7b");
    const [showColors, setShowColors] = useState(false);
    const [addNoteCapability, setAddNoteCapability] = useState(-1);

    useEffect(() => {
        document.body.style.overflow = "hidden";

        if (userCapability) {
            const addNotePermission = userCapability.find((item) =>
                item.component === "validationnote" && item.type === "page")?.permission;
            setAddNoteCapability(addNotePermission);
        }

        return () => {
            document.body.style.overflow = "";
        };
    }, [userCapability]);

    const disabled = addNoteCapability === 0;

    const onContinueClick = () => {
        closePopup();
        confirm(note, color);
    };

    return (
        <div className="oct-shared-popup" role="dialog" aria-modal="true" aria-labelledby="oct-edit-note-popup-title">
            <div className="oct-shared-popup__backdrop" onClick={closePopup} />

            <div className="oct-shared-popup__dialog oct-shared-popup__dialog--compact" onClick={(event) => event.stopPropagation()}>
                <div className="oct-shared-popup__header">
                    <h4 id="oct-edit-note-popup-title" className="oct-shared-popup__title oct-shared-popup__title--plain">Edit Note</h4>

                    <button type="button" className="oct-shared-popup__close" onClick={closePopup} aria-label="Close popup">
                        <X size={18} />
                    </button>
                </div>

                <div className="oct-shared-popup__body oct-shared-popup__body--form">
                    <div className="oct-shared-popup__field">
                        <label className="oct-shared-popup__note-badge" htmlFor="edit-note-text">
                            <FileText size={15} />
                            <span>Note</span>
                        </label>
                        <textarea
                            id="edit-note-text"
                            className="oct-shared-popup__textarea"
                            value={note}
                            disabled={disabled}
                            rows={6}
                            onChange={disabled ? undefined : (event) => setNote(event.target.value)}
                            placeholder="Enter your note here..."
                        />
                    </div>

                    <div className="oct-shared-popup__field oct-shared-popup__field--inline">
                        <button
                            type="button"
                            className="oct-shared-popup__color-trigger"
                            data-note-color={getNoteColorToken(color)}
                            onClick={() => setShowColors((current) => !current)}
                            disabled={disabled}
                        >
                            <Palette size={16} />
                            Select Color
                        </button>
                    </div>

                    {showColors && (
                        <div className="oct-shared-popup__color-grid">
                            {NOTE_COLORS.map((noteColor) => (
                                <button
                                    key={noteColor}
                                    type="button"
                                    className={`oct-shared-popup__color-swatch ${noteColor === color ? "is-selected" : ""}`}
                                    data-note-color={getNoteColorToken(noteColor)}
                                    onClick={() => {
                                        setColor(noteColor);
                                        setShowColors(false);
                                    }}
                                    aria-label={`Select ${noteColor} color`}
                                />
                            ))}
                        </div>
                    )}
                </div>

                <div className="oct-shared-popup__footer oct-shared-popup__footer--end">
                    <button type="button" className="btn oct-shared-popup__btn oct-shared-popup__btn--secondary" onClick={closePopup}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="btn oct-shared-popup__btn oct-shared-popup__btn--primary"
                        disabled={!note || disabled}
                        onClick={disabled ? undefined : onContinueClick}
                    >
                        Continue
                    </button>
                </div>
            </div>
        </div>
    );
}

function mapStateToProps(state) {
    const auth = state.auth;
    return {
        userCapability: auth.userCapability,
    };
}

export default connect(mapStateToProps)(EditNotePopup);
